import type { Attribution } from '@platform/types';

/**
 * First-touch marketing attribution. On the first page load of a tab that
 * carries a utm_* / ref param or an external referrer, record it in
 * sessionStorage (tab-scoped, cleared when the tab closes; disclosed in the
 * cookie policy). Signup + contact forms send it so we know which channel
 * produced each lead. Never stores PII and never throws.
 */
const KEY = 'first-touch';
const PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref'] as const;

export function captureAttribution(loc: Location = window.location, referrer = document.referrer): void {
  try {
    if (sessionStorage.getItem(KEY)) return;
    const q = new URLSearchParams(loc.search);
    const a: Attribution = {};
    for (const p of PARAMS) {
      const v = q.get(p)?.trim();
      if (v) a[p] = v.slice(0, 120);
    }
    let refHost = '';
    try {
      refHost = referrer ? new URL(referrer).hostname : '';
    } catch {
      refHost = '';
    }
    if (refHost && refHost !== loc.hostname) a.referrer = refHost.slice(0, 255);
    if (Object.keys(a).length === 0) return;
    a.landing = loc.pathname.slice(0, 255);
    sessionStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    // Storage blocked (private mode, sandbox) — attribution is optional.
  }
}

export function getAttribution(): Attribution | undefined {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Attribution) : undefined;
  } catch {
    return undefined;
  }
}
